export type CurrentInfiniteUserSearchFields = {
  fullName?: string | null;
  email?: string | null;
  phone?: string | null;
  phonePrefix?: string | null;
  customerId?: string | null;
  accountNumber?: number | null;
};

function digitsOnly(value: string): string {
  return value.replace(/\D/g, '');
}

/**
 * Matches a free-text query against a current Infinite member's name, email,
 * phone, customer id, or account number. Phone matching ignores spaces and
 * punctuation, and treats a local leading 0 as the same number stored without it.
 */
export function matchesCurrentInfiniteUserSearch(user: CurrentInfiniteUserSearchFields, query: string): boolean {
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) {
    return true;
  }

  const fullName = user.fullName?.trim().toLowerCase() ?? '';
  const email = user.email?.trim().toLowerCase() ?? '';
  const customerId = user.customerId?.trim().toLowerCase() ?? '';
  const accountNumber = user.accountNumber != null ? String(user.accountNumber) : '';
  const phoneDisplay = [user.phonePrefix, user.phone].filter(Boolean).join(' ').toLowerCase();

  if (
    email.includes(normalizedQuery) ||
    fullName.includes(normalizedQuery) ||
    customerId.includes(normalizedQuery) ||
    accountNumber.includes(normalizedQuery) ||
    phoneDisplay.includes(normalizedQuery)
  ) {
    return true;
  }

  const queryTokens = normalizedQuery.split(/\s+/).filter(Boolean);
  if (queryTokens.length > 1) {
    const nameAndEmail = `${fullName} ${email}`;
    if (queryTokens.every((token) => nameAndEmail.includes(token))) {
      return true;
    }
  }

  const queryDigits = digitsOnly(normalizedQuery);
  if (!queryDigits) {
    return false;
  }

  const phoneDigits = digitsOnly(user.phone ?? '');
  const prefixDigits = digitsOnly(user.phonePrefix ?? '');
  const combinedDigits = `${prefixDigits}${phoneDigits}`;
  const localDigits = phoneDigits ? `0${phoneDigits.replace(/^0/, '')}` : '';

  return phoneDigits.includes(queryDigits) || combinedDigits.includes(queryDigits) || localDigits.includes(queryDigits);
}
